import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Loader, Message, Container } from 'semantic-ui-react';
import { useAuth } from '../../context/AuthContext';
import { ConversationService } from './services/ConversationService';
import { Conversation, ConversationFilters, UserRole } from './types/conversation.types';
import GlassmorphismTheme from '../../styles/glassmorphism';
import ConversationList from './components/ConversationList';
import MessageTimeline from './components/MessageTimeline';
import InputBox from './components/InputBox';

const ConversationsPage: React.FC = () => {
  // ===== STATE =====
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [filters, setFilters] = useState<ConversationFilters>({ page: 1, limit: 20 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // ===== PERMISSIONS =====
  const getPermissions = useCallback(() => {
    const role = user?.roles?.[0] as UserRole || 'Viewer';
    return {
      Admin: { canView: true, canSend: true, canDownloadHistory: true, canEscalate: true, canViewAll: true },
      Manager: { canView: true, canSend: true, canDownloadHistory: false, canEscalate: true, canViewAll: true },
      Seller: { canView: true, canSend: true, canDownloadHistory: false, canEscalate: false, canViewAll: false },
      Viewer: { canView: true, canSend: false, canDownloadHistory: false, canEscalate: false, canViewAll: false }
    }[role];
  }, [user]);

  const permissions = getPermissions();

  // ===== FETCH CONVERSATIONS =====
  useEffect(() => {
    const loadConversations = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await ConversationService.getConversations(filters);
        setConversations(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar conversas');
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, [filters]);

  // ===== HANDLE RESIZE =====
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ===== HANDLE SELECT CONVERSATION =====
  const handleSelectConversation = (conversation: Conversation) => {
    if (permissions.canView) {
      setSelectedConversation(conversation);
    }
  };

  // ===== HANDLE FILTER CHANGE =====
  const handleFilterChange = (newFilters: Partial<ConversationFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters, page: 1 }));
  };

  // ===== PERMISSION CHECK =====
  if (!permissions.canView) {
    return (
      <Container style={{ marginTop: '50px', textAlign: 'center' }}>
        <Message error size="large">
          <Message.Header>Acesso Negado</Message.Header>
          <p>Você não tem permissão para acessar conversas. Entre em contato com um administrador.</p>
        </Message>
      </Container>
    );
  }

  // ===== LAYOUT =====
  return (
    <div style={{
      background: GlassmorphismTheme.colors.gradients.primary,
      minHeight: '100vh',
      padding: GlassmorphismTheme.spacing.md,
    }}>
      <Grid 
        stackable 
        style={{ height: '100%', gap: GlassmorphismTheme.spacing.md }}
        columns={isMobile ? 1 : 2}
      >
        {/* SIDEBAR - CONVERSATION LIST */}
        <Grid.Column width={isMobile ? 16 : 5} style={{ marginBottom: isMobile ? GlassmorphismTheme.spacing.lg : 0 }}>
          {loading && !conversations.length ? (
            <Loader active content="Carregando conversas..." />
          ) : error ? (
            <Message error content={error} />
          ) : (
            <ConversationList
              conversations={conversations}
              selectedId={selectedConversation?.id}
              onSelect={handleSelectConversation}
              onFilterChange={handleFilterChange}
              filters={filters}
            />
          )}
        </Grid.Column>

        {/* MAIN - MESSAGE TIMELINE + INPUT */}
        <Grid.Column width={isMobile ? 16 : 11}>
          {selectedConversation ? (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              ...GlassmorphismTheme.components.glassCard,
            }}>
              {/* HEADER */}
              <div style={{
                padding: GlassmorphismTheme.spacing.md,
                borderBottom: GlassmorphismTheme.borders.glassLight,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <div>
                  <h3 style={{ margin: '0 0 5px 0', color: '#2c3e50' }}>
                    {selectedConversation.nomeCliente}
                  </h3>
                  <small style={{ color: '#7f8c8d' }}>
                    {selectedConversation.cnpj && `CNPJ: ${selectedConversation.cnpj}`}
                  </small>
                </div>
                <span style={{
                  ...GlassmorphismTheme.components.badge,
                  textTransform: 'capitalize'
                }}>
                  {selectedConversation.statusFluxo}
                </span>
              </div>

              {/* TIMELINE */}
              <div style={{
                flex: 1,
                overflowY: 'auto',
                padding: GlassmorphismTheme.spacing.md,
              }}>
                <MessageTimeline 
                  clienteId={selectedConversation.clienteId}
                  permissions={permissions}
                />
              </div>

              {/* INPUT BOX */}
              {permissions.canSend && (
                <div style={{
                  borderTop: GlassmorphismTheme.borders.glassLight,
                  padding: GlassmorphismTheme.spacing.md,
                }}>
                  <InputBox 
                    clienteId={selectedConversation.clienteId}
                    onMessageSent={() => {
                      // Refresh timeline
                    }}
                  />
                </div>
              )}
            </div>
          ) : (
            <Message 
              info 
              style={{
                ...GlassmorphismTheme.components.glassCard,
                textAlign: 'center'
              }}
            >
              <Message.Header>Selecione uma conversa</Message.Header>
              <p>Escolha um cliente na lista para visualizar o histórico de mensagens</p>
            </Message>
          )}
        </Grid.Column>
      </Grid>
    </div>
  );
};

export default ConversationsPage;
